import { useMemo } from 'react';
import { Card, Row, Col, Spinner, ProgressBar, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faTriangleExclamation, faRotate } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { useForecast } from '../../features/machine-intervals/hooks/useMachineIntervals.js';

const REASON_LABEL = {
  COLOR_CHANGE: 'colour change',
  COLOR_CHANGE_ON_FLY: 'colour change on the fly',
  PRODUCT_CHANGEOVER: 'product changeover',
  PHASE_CHANGEOVER: 'phase changeover',
  DRUM_CHANGE: 'drum change'
};

// Where the estimate came from. An operator's own record and a figure borrowed
// from the machine are not the same claim, so they must not look the same.
const BASIS_LABEL = {
  operator: { text: "from this operator's history", cls: 'text-success' },
  machine: { text: 'machine median', cls: 'text-info' },
  global: { text: 'overall median', cls: 'text-warning' }
};

const fmtDur = sec => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

const fmtKm = m => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`);

/**
 * Estimated duration of a planned run on one machine.
 *
 *   Σ(metres / speed)  +  Σ(transition × what that transition costs here)
 *
 * Both halves are measured history, never nominal figures. When a piece is
 * missing the panel says so instead of quietly reporting a smaller number — an
 * estimate that hides its gaps is worse than one that admits them.
 */
const ForecastPanel = ({ machineId, tokens = [], operator = null, previousToken = null, className = '' }) => {
  const { data, isLoading, isError } = useForecast({ machineId, operator, tokens, previousToken });
  const f = data?.data || null;

  const totalMeters = useMemo(
    () => tokens.reduce((s, t) => s + (Number(t.lengthM) || 0), 0),
    [tokens]
  );

  const transitions = useMemo(
    () => (f?.steps || []).filter(s => s.kind === 'transition' && s.sec > 0),
    [f]
  );

  const byReason = useMemo(() => {
    const m = new Map();
    for (const t of transitions) {
      const prev = m.get(t.reason) || { count: 0, sec: 0, basis: t.basis };
      m.set(t.reason, { count: prev.count + 1, sec: prev.sec + t.sec, basis: t.basis });
    }
    return [...m.entries()].sort((a, b) => b[1].sec - a[1].sec);
  }, [transitions]);

  if (!machineId || !tokens.length) return null;

  if (isLoading) {
    return (
      <Card className={className}>
        <Card.Body className="d-flex align-items-center gap-2 py-3">
          <Spinner animation="border" size="sm" variant="primary" />
          <span className="text-700 fs-9">Calculating estimate...</span>
        </Card.Body>
      </Card>
    );
  }

  if (isError || !f) {
    return (
      <Card className={className}>
        <Card.Body className="py-3 text-700 fs-9">The estimate could not be calculated.</Card.Body>
      </Card>
    );
  }

  const runPct = f.totalSec > 0 ? Math.round((f.runningSec / f.totalSec) * 100) : 0;
  const finishesAt = dayjs().add(f.totalSec, 'second');

  return (
    <Card className={className}>
      <Card.Header className="py-2 d-flex align-items-center justify-content-between">
        <h6 className="mb-0">
          <FontAwesomeIcon icon={faClock} className="me-2 text-primary" />
          Estimate for the {String(machineId).toUpperCase()} queue
        </h6>
        {operator ? <span className="fs-10 text-700">operator: {operator}</span> : null}
      </Card.Header>

      <Card.Body className="py-3">
        <Row className="g-3 mb-3">
          <Col xs={6} md={3}>
            <p className="text-700 fs-10 mb-1">Total time</p>
            <h4 className="mb-0 text-primary">{fmtDur(f.totalSec)}</h4>
            {f.totalSec > 0 ? (
              <span className="text-700 fs-10">done ~{finishesAt.format('DD MMM, HH:mm')}</span>
            ) : null}
          </Col>
          <Col xs={6} md={3}>
            <p className="text-700 fs-10 mb-1">Production</p>
            <h4 className="mb-0 text-success">{fmtDur(f.runningSec)}</h4>
            <span className="text-700 fs-10">
              {fmtKm(totalMeters)} across {tokens.length} {tokens.length === 1 ? 'drum' : 'drums'}
            </span>
          </Col>
          <Col xs={6} md={3}>
            <p className="text-700 fs-10 mb-1">Setups</p>
            <h4 className="mb-0 text-warning">{fmtDur(f.changeoverSec)}</h4>
            <span className="text-700 fs-10">
              {transitions.length} {transitions.length === 1 ? 'transition' : 'transitions'}
            </span>
          </Col>
          <Col xs={6} md={3}>
            <p className="text-700 fs-10 mb-1">Spent producing</p>
            <h4 className="mb-0 text-info">{runPct}%</h4>
          </Col>
        </Row>

        {/* production vs setup, at a glance — on short drums setup dominates */}
        <ProgressBar className="mb-3 bg-body-tertiary" style={{ height: 10 }}>
          <ProgressBar now={runPct} variant="success" key="run" />
          <ProgressBar now={100 - runPct} variant="warning" key="setup" />
        </ProgressBar>

        {byReason.length ? (
          <div className="mb-2">
            {byReason.map(([reason, agg]) => {
              const basis = BASIS_LABEL[agg.basis] || null;
              return (
                <div
                  key={reason}
                  className="d-flex align-items-center justify-content-between border-bottom border-200 py-2"
                >
                  <div className="d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faRotate} className="fs-10 text-warning" />
                    <span className="fs-9 text-900">
                      {agg.count} × {REASON_LABEL[reason] || reason}
                    </span>
                    {basis ? (
                      <span className={`fs-10 ${basis.cls}`} title="Where this estimate comes from">
                        · {basis.text}
                      </span>
                    ) : null}
                  </div>
                  <span className="fs-9 fw-semibold font-monospace">{fmtDur(agg.sec)}</span>
                </div>
              );
            })}
          </div>
        ) : null}

        {!f.complete ? (
          <Alert variant="warning" className="mb-0 py-2 px-3 fs-10">
            <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
            Incomplete estimate — data missing for {f.unknowns.length}{' '}
            {f.unknowns.length === 1 ? 'item' : 'items'}. The real time will be longer.
            <div className="mt-1 text-700">
              {f.unknowns.slice(0, 4).map((u, i) => (
                <div key={i}>
                  {u.kind === 'speed'
                    ? `no speed history for ${u.productLabel || u.tokenId || 'a drum'}`
                    : `no history for ${REASON_LABEL[u.reason] || u.reason}`}
                </div>
              ))}
              {f.unknowns.length > 4 ? <div>+{f.unknowns.length - 4} more</div> : null}
            </div>
          </Alert>
        ) : null}

        {f.basedOn ? (
          <div className="text-700 fs-10 mt-2">
            Based on {f.basedOn.intervals}{' '}
            {f.basedOn.intervals === 1 ? 'interval' : 'intervals'} and {f.basedOn.metrics}{' '}
            {f.basedOn.metrics === 1 ? 'execution' : 'executions'} from the last{' '}
            {f.basedOn.windowDays} days.
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
};

export default ForecastPanel;
